import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

/**
 * Breadcrumb Component
 * 
 * @param {Array} items - Array of breadcrumb items
 *   Each item: { label: string, href: string, current?: boolean }
 * @param {string} variant - 'dark' (navy bg) or 'light' (cream/white bg)
 */
const Breadcrumb = ({ items = [], variant = 'dark' }) => {
  const baseUrl = 'https://pacificcrestprop.com';
  
  // Generate JSON-LD structured data
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": item.href.startsWith('http') ? item.href : `${baseUrl}${item.href}`
    }))
  };

  const textColor = variant === 'dark' ? 'text-white/60' : 'text-gray-500';
  const activeColor = variant === 'dark' ? 'text-white' : 'text-[#1F3B60]';
  const hoverColor = variant === 'dark' ? 'hover:text-white' : 'hover:text-[#1F3B60]';
  const separatorColor = variant === 'dark' ? 'text-white/30' : 'text-gray-300';
  const bgColor = variant === 'dark' ? 'bg-[#1F3B60]/50' : 'bg-white/50';

  return (
    <>
      {/* JSON-LD Structured Data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Breadcrumb UI */}
      <nav 
        aria-label="Breadcrumb" 
        className={`${bgColor} backdrop-blur-sm border-b ${variant === 'dark' ? 'border-white/10' : 'border-gray-200/50'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ol className="flex items-center space-x-2 py-3 text-sm overflow-x-auto">
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              const isCurrent = item.current || isLast;

              return (
                <li key={item.href} className="flex items-center whitespace-nowrap">
                  {index === 0 && (
                    <Home className={`w-4 h-4 mr-1.5 ${textColor}`} />
                  )}
                  
                  {isCurrent ? (
                    <span 
                      className={`font-medium ${activeColor}`}
                      aria-current="page"
                    >
                      {item.label}
                    </span>
                  ) : (
                    <Link
                      to={item.href}
                      className={`${textColor} ${hoverColor} transition-colors duration-200`}
                    >
                      {item.label}
                    </Link>
                  )}

                  {!isLast && (
                    <ChevronRight className={`w-4 h-4 mx-2 ${separatorColor}`} />
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumb;
